import React, { useState, useEffect, useMemo, useCallback } from "react";
import { MessageList } from "./MessageList";
import { MessageInput } from "./MessageInput";
import { ConversationList } from "./ConversationList";
import { useChat } from "@/hooks/useChat";
import { useRole } from "@/hooks/useRole";
import { Button } from "@/components/ui/button";
import { Menu, ArrowLeft } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface ChatContainerProps {
  initialConversationId?: string;
}

export const ChatContainer: React.FC<ChatContainerProps> = ({ initialConversationId }) => {
  const { role } = useRole();
  const isMobile = useIsMobile();
  const [showConversationList, setShowConversationList] = useState(!isMobile);
  
  const {
    conversations, 
    messages, 
    currentConversationId, 
    loading,
    sendingMessage,
    sendMessage,
    selectConversation,
    loadConversations,
    forceReloadMessages,
    loadSpecificConversation
  } = useChat(initialConversationId);

  // Get the current conversation - memoizado
  const currentConversation = useMemo(() => {
    return conversations.find(conv => conv.id === currentConversationId);
  }, [conversations, currentConversationId]);

  // Determine the recipient ID based on user role - memoizado
  const recipientId = useMemo(() => {
    if (!currentConversation || !role) {
      return "";
    }
    
    return role === "mentor" 
      ? currentConversation.player_id 
      : currentConversation.mentor_id;
  }, [currentConversation, role]);

  // Handle sending messages - useCallback
  const handleSendMessage = useCallback((message: string) => {
    if (!recipientId || !currentConversationId) {
      console.error("❌ [ChatContainer] Missing recipientId or conversationId");
      return;
    }
    
    sendMessage(message, recipientId);
  }, [recipientId, currentConversationId, sendMessage]);

  // Get conversation header name - memoizado
  const headerName = useMemo(() => {
    if (!currentConversation) return "Selecione uma conversa";
    return role === "mentor" 
      ? currentConversation.player_name 
      : currentConversation.mentor_name;
  }, [currentConversation, role]);

  // Handle conversation selection on mobile - useCallback
  const handleSelectConversation = useCallback((id: string) => {
    selectConversation(id);
    if (isMobile) {
      setShowConversationList(false);
    }
  }, [selectConversation, isMobile]);

  // Close conversation list on mobile when conversation is selected
  useEffect(() => {
    if (isMobile && currentConversationId) {
      setShowConversationList(false);
    }
  }, [currentConversationId, isMobile]);

  // SIMPLIFIED: Check for new conversation signal only once
  useEffect(() => {
    const newConversationCreated = localStorage.getItem('newConversationCreated');
    const newConversationId = localStorage.getItem('newConversationId');
    
    if (newConversationCreated === 'true' && newConversationId) {
      // Clear the signals immediately
      localStorage.removeItem('newConversationCreated');
      localStorage.removeItem('newConversationId');
      
      // Reload conversations
      loadConversations();
      
      // If this matches current conversation, reload messages
      if (currentConversationId === newConversationId) {
        setTimeout(() => {
          forceReloadMessages();
        }, 1000);
      }
    }
  }, []); // Empty dependency array - runs only once

  // FALLBACK: Busca conversa específica se não encontrada (otimizado)
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (currentConversationId && !currentConversation && !loading && conversations.length > 0) {
      timer = setTimeout(() => {
        console.log("🚨 [ChatContainer] Conversa não encontrada - buscando diretamente");
        loadSpecificConversation(currentConversationId);
      }, 3000); // Delay maior para evitar chamadas excessivas
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [currentConversationId, currentConversation, loading, conversations.length, loadSpecificConversation]);

  return (
    <div className="flex h-full bg-[#0A0F1C] text-white">
      {/* Conversation List */}
      <div className={`
        ${isMobile 
          ? (showConversationList ? 'w-full' : 'hidden') 
          : 'w-80 border-r border-[#1A2332]'
        }
        flex flex-col
      `}>
        {/* Removed duplicate title - using page title instead */}
        
        <ConversationList
          conversations={conversations}
          selectedConversationId={currentConversationId}
          onSelectConversation={handleSelectConversation}
          loading={loading}
          userRole={role}
        />
      </div>

      {/* Chat Area */}
      <div className={`
        ${isMobile 
          ? (showConversationList ? 'hidden' : 'w-full') 
          : 'flex-1'
        }
        flex flex-col
      `}>
        {/* Header */}
        <div className="p-4 border-b border-[#1A2332] flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isMobile && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowConversationList(true)}
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <h3 className="font-semibold">{headerName}</h3>
          </div>
          
          {isMobile && currentConversationId && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowConversationList(true)}
              className="text-gray-400 hover:text-white"
            >
              <Menu className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-hidden">
          {currentConversationId ? (
            <MessageList 
              messages={messages} 
              loading={loading}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400">
              <p>Selecione uma conversa para começar</p>
            </div>
          )}
        </div>

        {/* Message Input */}
        {currentConversationId && (
          <MessageInput
            onSendMessage={handleSendMessage}
            disabled={sendingMessage || !recipientId}
            placeholder={
              !recipientId 
                ? "Carregando conversa..." 
                : "Digite sua mensagem..."
            }
          />
        )}
      </div>
    </div>
  );
};
